import React, { useState, useEffect, useCallback } from "react";

const API = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
  ? "http://localhost:5000"
  : (process.env.REACT_APP_API_URL || "http://localhost:5000");

// ── Helpers ────────────────────────────────────────────────────────────────────
const adminHeaders = (token) => ({
  "Content-Type": "application/json",
  "admin-password": token,
});

// ── Sub-components ─────────────────────────────────────────────────────────────
function Alert({ type, msg, onClose }) {
  if (!msg) return null;
  const colors = {
    error:   { bg: "var(--surface2)", color: "var(--red)", border: "var(--red)" },
    success: { bg: "var(--surface2)", color: "var(--green)", border: "var(--green)" },
    info:    { bg: "var(--surface2)", color: "var(--accent)", border: "var(--accent)" },
  };
  const c = colors[type] || colors.info;
  return (
    <div style={{
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      padding: "10px 14px", borderRadius: 8, marginBottom: 16,
      display: "flex", justifyContent: "space-between", alignItems: "center",
    }}>
      <span>{msg}</span>
      {onClose && (
        <button onClick={onClose} style={{ background: "none", border: "none", color: c.color, cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</button>
      )}
    </div>
  );
}

function Modal({ title, onClose, children, style }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16,
    }}>
      <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 16, width: "100%", maxWidth: 420, overflow: "hidden", ...style }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 16, color: "var(--text)" }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontSize: 12, color: "var(--muted)", marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</label>
      {children}
    </div>
  );
}

function Input({ ...props }) {
  return (
    <input {...props} style={{
      width: "100%", padding: "10px 12px", borderRadius: 8,
      background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text)",
      fontSize: 14, boxSizing: "border-box", outline: "none",
      ...props.style,
    }} />
  );
}

function Btn({ variant = "primary", children, ...props }) {
  const styles = {
    primary: { background: "var(--accent)", border: "1px solid #2ea043", color: "#fff" },
    danger:  { background: "var(--surface2)", border: "1px solid rgba(248,113,113,0.4)", color: "var(--red)" },
    ghost:   { background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "var(--text)" },
    blue:    { background: "var(--surface2)", border: "1px solid rgba(59,130,246,0.4)", color: "var(--accent)" },
  };
  return (
    <button {...props} style={{
      padding: "8px 16px", borderRadius: 8, cursor: "pointer",
      fontSize: 13, fontWeight: 600, transition: "opacity 0.15s",
      ...styles[variant], ...(props.disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}),
      ...props.style,
    }}>
      {children}
    </button>
  );
}

// ── Login Screen ──────────────────────────────────────────────────────────────
function AdminLogin({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const res  = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      onLogin(data.token);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "linear-gradient(135deg, var(--bg) 0%, var(--surface) 100%)", padding: 16,
    }}>
      <div style={{
        width: "100%", maxWidth: 380,
        background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 20,
        overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
      }}>
        <div style={{ padding: "28px 28px 0", textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>Admin</div>
          <h1 style={{ margin: 0, fontSize: 22, color: "var(--text)", fontWeight: 700 }}>Admin Panel</h1>
          <p style={{ color: "var(--muted)", fontSize: 13, marginTop: 6 }}>Enter your admin credentials to continue</p>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: 28 }}>
          <Alert type="error" msg={error} onClose={() => setError("")} />
          <Field label="Username">
            <Input type="text" placeholder="admin" value={username} onChange={e => setUsername(e.target.value)} required />
          </Field>
          <Field label="Password">
            <Input type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required />
          </Field>
          <Btn variant="primary" type="submit" disabled={loading} style={{ width: "100%", padding: "12px", fontSize: 14 }}>
            {loading ? "Verifying..." : "Sign In to Admin"}
          </Btn>
        </form>
      </div>
    </div>
  );
}

function OverviewTab({ token }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/stats`, { headers: adminHeaders(token) });
      const data = await res.json();
      setStats(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>Loading stats...</div>;
  if (!stats) return null;

  const statCards = [
    { label: "Total Users", value: stats.totalUsers, emoji: "👥", color: "var(--accent)" },
    { label: "System Balance", value: stats.totalPoints.toLocaleString() + " pts", emoji: "💰", color: "var(--accent2)" },
    { label: "Locked Points", value: stats.totalLocked.toLocaleString() + " pts", emoji: "🔒", color: "var(--accent)" },
    { label: "Total Bets", value: stats.totalBets, emoji: "🎯", color: "var(--green)" },
    { label: "Pending Payouts", value: stats.pendingWithdrawals, emoji: "💸", color: "var(--red)" },
    { label: "Pending Deposits", value: stats.pendingDeposits || 0, emoji: "📥", color: "var(--accent)" },
    { label: "Total Paid Out", value: stats.totalPaid.toLocaleString() + " pts", emoji: "✅", color: "var(--green)" },
  ];

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 20, marginBottom: 30 }}>
        {statCards.map(s => (
          <div key={s.label} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, padding: 24, boxShadow: "0 4px 12px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px" }}>{s.label}</span>
              <span style={{ fontSize: 20 }}>{s.emoji}</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 900, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>
      <Btn variant="ghost" onClick={fetchStats}>🔄 Refresh Stats</Btn>
    </div>
  );
}

// ── Users Tab ─────────────────────────────────────────────────────────────────
function UsersTab({ token }) {
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [alert, setAlert]       = useState({ type: "", msg: "" });
  const [editUser, setEditUser] = useState(null);
  const [editPoints, setEditPoints] = useState("");
  const [search, setSearch]     = useState("");
  const [trackedUser, setTrackedUser] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/admin/users`, { headers: adminHeaders(token) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setUsers(data);
    } catch (err) {
      setAlert({ type: "error", msg: err.message });
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleInspect = async (username) => {
    setTrackingLoading(true);
    setTrackedUser(null);
    try {
      const res = await fetch(`${API}/admin/users/${username}/track`, { headers: adminHeaders(token) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setTrackedUser(data);
    } catch (err) {
      setAlert({ type: "error", msg: err.message });
    }
    setTrackingLoading(false);
  };

  const handleStatusChange = async (username, status) => {
    try {
      const res = await fetch(`${API}/admin/fraud/user/action`, {
        method: "POST",
        headers: adminHeaders(token),
        body: JSON.stringify({ username, action: status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setAlert({ type: "success", msg: data.message });
      // Re-inspect to update details
      handleInspect(username);
      fetchUsers();
    } catch (err) {
      setAlert({ type: "error", msg: err.message });
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const res  = await fetch(`${API}/admin/update-user`, {
        method: "POST", headers: adminHeaders(token),
        body: JSON.stringify({ username: editUser.name, points: Number(editPoints) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setAlert({ type: "success", msg: data.message });
      setEditUser(null); fetchUsers();
    } catch (err) { setAlert({ type: "error", msg: err.message }); }
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Delete user "${username}"? This cannot be undone.`)) return;
    try {
      const res  = await fetch(`${API}/admin/users/${username}`, {
        method: "DELETE", headers: adminHeaders(token),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setAlert({ type: "success", msg: data.message });
      fetchUsers();
    } catch (err) { setAlert({ type: "error", msg: err.message }); }
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center" }}>
        <Input placeholder="🔍 Search users..." value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1 }} />
        <Btn variant="ghost" onClick={fetchUsers}>🔄 Refresh</Btn>
      </div>

      <Alert type={alert.type} msg={alert.msg} onClose={() => setAlert({ type: "", msg: "" })} />

      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--surface2)" }}>
              {["Rank", "Username", "Points", "Status", "Actions"].map(h => (
                <th key={h} style={{ padding: "12px 16px", fontSize: 12, color: "var(--muted)", fontWeight: 600, textAlign: h === "Actions" ? "right" : "left" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>Loading users...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>No users found.</td></tr>
            ) : filtered.map((user, idx) => (
              <tr key={user.name} style={{ borderTop: "1px solid #21262d" }}>
                <td style={{ padding: "12px 16px", fontSize: 13, color: "var(--muted)" }}>
                  {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `#${idx + 1}`}
                </td>
                <td style={{ padding: "12px 16px", fontSize: 14, color: "var(--text)", fontWeight: 600 }}>{user.name}</td>
                <td style={{ padding: "12px 16px", fontSize: 14, color: "var(--accent2)", fontWeight: 800 }}>
                  {(user.points || 0).toLocaleString()}
                </td>
                <td style={{ padding: "12px 16px" }}>
                  <span style={{
                    fontSize: 11, fontWeight: "bold", padding: "2px 6px", borderRadius: 4,
                    background: user.status === "active" ? "rgba(46,160,67,0.15)" : "rgba(248,113,113,0.15)",
                    color: user.status === "active" ? "var(--green)" : "var(--red)",
                    border: `1px solid ${user.status === "active" ? "rgba(46,160,67,0.3)" : "rgba(248,113,113,0.3)"}`
                  }}>
                    {(user.status || "active").toUpperCase()}
                  </span>
                </td>
                <td style={{ padding: "12px 16px", textAlign: "right" }}>
                  <Btn variant="blue" style={{ marginRight: 8 }} onClick={() => handleInspect(user.name)}>
                    🔍 Inspect
                  </Btn>
                  <Btn variant="ghost" style={{ marginRight: 8 }} onClick={() => { setEditUser(user); setEditPoints(user.points || 0); }}>
                    ✏️ Edit
                  </Btn>
                  <Btn variant="danger" onClick={() => handleDelete(user.name)}>🗑️ Delete</Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, fontSize: 12, color: "var(--muted)" }}>
        {filtered.length} user{filtered.length !== 1 ? "s" : ""} total
      </div>

      {/* Inspect / Track User Modal */}
      {trackingLoading && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div style={{ color: "var(--accent)", fontSize: 16, fontWeight: "bold" }}>Fetching complete audit trail...</div>
        </div>
      )}

      {trackedUser && (
        <Modal title={`User Track Audit: ${trackedUser.profile.name}`} onClose={() => setTrackedUser(null)} style={{ maxWidth: 720, width: "100%" }}>
          <div style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: 8 }}>
            {/* Top Cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
              <div style={{ background: "var(--surface)", border: "1px solid var(--border)", padding: 12, borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: "bold" }}>STATUS</div>
                <div style={{ fontSize: 16, fontWeight: "bold", marginTop: 4, color: trackedUser.profile.status === "active" ? "var(--green)" : "var(--red)" }}>
                  {trackedUser.profile.status.toUpperCase()}
                </div>
              </div>
              <div style={{ background: "var(--surface)", border: "1px solid var(--border)", padding: 12, borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: "bold" }}>POINTS</div>
                <div style={{ fontSize: 16, fontWeight: "bold", marginTop: 4, color: "var(--accent2)" }}>
                  {trackedUser.profile.points.toLocaleString()}
                </div>
              </div>
              <div style={{ background: "var(--surface)", border: "1px solid var(--border)", padding: 12, borderRadius: 8, textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: "bold" }}>LOCKED POINTS</div>
                <div style={{ fontSize: 16, fontWeight: "bold", marginTop: 4, color: "var(--accent)" }}>
                  {trackedUser.profile.lockedPoints.toLocaleString()}
                </div>
              </div>
            </div>

            {/* Grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
              {/* Profile Details */}
              <div style={{ background: "var(--surface)", border: "1px solid var(--border)", padding: 16, borderRadius: 10 }}>
                <h4 style={{ margin: "0 0 12px", borderBottom: "1px solid var(--border)", paddingBottom: 6, color: "var(--accent)" }}>Identity & Session</h4>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
                  <div><strong style={{ color: "var(--muted)" }}>Email:</strong> {trackedUser.profile.email}</div>
                  <div><strong style={{ color: "var(--muted)" }}>Joined:</strong> {new Date(trackedUser.profile.createdAt).toLocaleString()}</div>
                  <div><strong style={{ color: "var(--muted)" }}>Last Active:</strong> {trackedUser.profile.lastActive ? new Date(trackedUser.profile.lastActive).toLocaleString() : "N/A"}</div>
                  <div><strong style={{ color: "var(--muted)" }}>Last IP:</strong> <code style={{ color: "#aaa" }}>{trackedUser.profile.lastIp || "N/A"}</code></div>
                  <div style={{ wordBreak: "break-all" }}>
                    <strong style={{ color: "var(--muted)" }}>Last Device:</strong> <span style={{ fontSize: 11, color: "#888" }}>{trackedUser.profile.lastDevice || "N/A"}</span>
                  </div>
                </div>
              </div>

              {/* Activity metrics */}
              <div style={{ background: "var(--surface)", border: "1px solid var(--border)", padding: 16, borderRadius: 10 }}>
                <h4 style={{ margin: "0 0 12px", borderBottom: "1px solid var(--border)", paddingBottom: 6, color: "var(--accent)" }}>Activity Metrics</h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 12px", fontSize: 13 }}>
                  <div><strong style={{ color: "var(--muted)" }}>Total Bets:</strong> {trackedUser.betting.totalBets}</div>
                  <div><strong style={{ color: "var(--muted)" }}>Win Rate:</strong> <span style={{ color: "var(--green)", fontWeight: "bold" }}>{trackedUser.betting.winRate}</span></div>
                  <div><strong style={{ color: "var(--muted)" }}>Bets Won:</strong> {trackedUser.betting.wonBets}</div>
                  <div><strong style={{ color: "var(--muted)" }}>Bets Lost:</strong> {trackedUser.betting.lostBets}</div>
                  <div><strong style={{ color: "var(--muted)" }}>Bets Pending:</strong> {trackedUser.betting.pendingBets}</div>
                  <div><strong style={{ color: "var(--muted)" }}>Wagered:</strong> {trackedUser.betting.totalWagered.toLocaleString()} pts</div>
                  <div><strong style={{ color: "var(--muted)" }}>Challenges:</strong> {trackedUser.challenges.totalChallenges} ({trackedUser.challenges.wonChallenges} won)</div>
                  <div><strong style={{ color: "var(--muted)" }}>Fantasy Teams:</strong> {trackedUser.fantasy.totalTeams} (Best: {trackedUser.fantasy.bestScore} pts)</div>
                </div>
              </div>
            </div>

            {/* Financial History */}
            <div style={{ background: "var(--surface)", border: "1px solid var(--border)", padding: 16, borderRadius: 10, marginBottom: 20 }}>
              <h4 style={{ margin: "0 0 12px", borderBottom: "1px solid var(--border)", paddingBottom: 6, color: "var(--accent)" }}>Financial History Summary</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: "bold", color: "var(--green)", marginBottom: 8 }}>📥 DEPOSITS (Total: {trackedUser.financials.totalDeposited.toLocaleString()} pts)</div>
                  {trackedUser.financials.depositHistory.length === 0 ? (
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>No deposits recorded</div>
                  ) : (
                    trackedUser.financials.depositHistory.map(d => (
                      <div key={d._id} style={{ fontSize: 11, borderBottom: "1px solid var(--border)", padding: "6px 0", display: "flex", justifyContent: "space-between" }}>
                        <span>{new Date(d.timestamp).toLocaleDateString()}</span>
                        <span style={{ fontWeight: "bold", color: d.status === "approved" ? "var(--green)" : "var(--muted)" }}>+{d.amount} ({d.status.toUpperCase()})</span>
                      </div>
                    ))
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: "bold", color: "var(--red)", marginBottom: 8 }}>💸 WITHDRAWALS (Total: {trackedUser.financials.totalWithdrawn.toLocaleString()} pts)</div>
                  {trackedUser.financials.withdrawalHistory.length === 0 ? (
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>No withdrawals recorded</div>
                  ) : (
                    trackedUser.financials.withdrawalHistory.map(w => (
                      <div key={w._id} style={{ fontSize: 11, borderBottom: "1px solid var(--border)", padding: "6px 0", display: "flex", justifyContent: "space-between" }}>
                        <span>{new Date(w.timestamp).toLocaleDateString()}</span>
                        <span style={{ fontWeight: "bold", color: w.status === "approved" ? "var(--green)" : w.status === "pending" ? "var(--accent)" : "var(--red)" }}>-{w.amount} ({w.status.toUpperCase()})</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Fraud Alerts */}
            <div style={{ background: "var(--surface)", border: "1px solid var(--border)", padding: 16, borderRadius: 10, marginBottom: 20 }}>
              <h4 style={{ margin: "0 0 12px", borderBottom: "1px solid var(--border)", paddingBottom: 6, color: "var(--red)" }}>🚨 Security & Fraud Alerts ({trackedUser.fraud.alertsCount})</h4>
              {trackedUser.fraud.recentAlerts.length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--muted)" }}>No fraud warnings triggered. Good reputation.</div>
              ) : (
                trackedUser.fraud.recentAlerts.map(a => (
                  <div key={a._id} style={{ fontSize: 12, borderBottom: "1px solid var(--border)", padding: "6px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <span style={{ color: "var(--muted)", marginRight: 8 }}>{new Date(a.timestamp).toLocaleDateString()}</span>
                      <span style={{ fontWeight: "bold" }}>{a.ruleTriggered}</span>
                    </div>
                    <span style={{ fontWeight: "bold", color: a.severity === "High" ? "var(--red)" : "var(--accent2)", background: "rgba(0,0,0,0.3)", padding: "2px 6px", borderRadius: 4, fontSize: 10 }}>
                      {a.severity.toUpperCase()}
                    </span>
                  </div>
                ))
              )}
            </div>

            {/* Administrative controls */}
            <div style={{ background: "var(--surface2)", padding: 16, borderRadius: 10 }}>
              <h4 style={{ margin: "0 0 12px", color: "var(--accent2)" }}>Mitigation & Action Center</h4>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {trackedUser.profile.status !== "active" && (
                  <Btn variant="primary" onClick={() => handleStatusChange(trackedUser.profile.name, "active")}>🔓 Reactivate Account</Btn>
                )}
                {trackedUser.profile.status !== "suspended" && (
                  <Btn variant="blue" onClick={() => handleStatusChange(trackedUser.profile.name, "suspended")}>🔒 Suspend Account</Btn>
                )}
                {trackedUser.profile.status !== "banned" && (
                  <Btn variant="danger" onClick={() => handleStatusChange(trackedUser.profile.name, "banned")}>🚫 Permanently Ban</Btn>
                )}
                <Btn variant="ghost" onClick={() => { setEditUser(trackedUser.profile); setEditPoints(trackedUser.profile.points); setTrackedUser(null); }}>✏️ Modify Wallet Points</Btn>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {editUser && (
        <Modal title={`Edit User: ${editUser.name}`} onClose={() => setEditUser(null)}>
          <form onSubmit={handleUpdate}>
            <Field label="Current Points">
              <div style={{ padding: "10px 12px", background: "var(--bg)", borderRadius: 8, color: "var(--muted)", fontSize: 14, border: "1px solid #21262d" }}>
                {(editUser.points || 0).toLocaleString()} pts
              </div>
            </Field>
            <Field label="Set New Points">
              <Input
                type="number" min={0}
                value={editPoints}
                onChange={e => setEditPoints(e.target.value)}
                placeholder="Enter new point value"
              />
            </Field>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn variant="ghost" type="button" onClick={() => setEditUser(null)} style={{ flex: 1 }}>Cancel</Btn>
              <Btn variant="primary" type="submit" style={{ flex: 1 }}>Save Changes</Btn>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function WithdrawalsTab({ token }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [alert, setAlert]       = useState({ type: "", msg: "" });
  const [remark, setRemark]     = useState({}); // Map of requestId -> remark string

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/admin/withdrawals`, { headers: adminHeaders(token) });
      const data = await res.json();
      setRequests(data);
    } catch (err) { setAlert({ type: "error", msg: "Failed to fetch" }); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleAction = async (requestId, action) => {
    try {
      const res = await fetch(`${API}/admin/withdrawal/action`, {
        method: "POST", headers: adminHeaders(token),
        body: JSON.stringify({ requestId, action, remark: remark[requestId] || "" })
      });
      const data = await res.json();
      if (res.ok) {
        setAlert({ type: "success", msg: data.message });
        fetchRequests();
      } else {
        setAlert({ type: "error", msg: data.message });
      }
    } catch (err) { setAlert({ type: "error", msg: "Action failed" }); }
  };

  return (
    <div>
      <Alert type={alert.type} msg={alert.msg} onClose={() => setAlert({ type: "", msg: "" })} />
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--surface2)" }}>
              {["User", "Amount", "Method", "Details", "Status", "Actions"].map(h => (
                <th key={h} style={{ padding: "12px 16px", fontSize: 12, color: "var(--muted)", textAlign: "left" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} style={{ padding: 20, textAlign: "center" }}>Loading...</td></tr> : requests.map(req => (
              <tr key={req._id} style={{ borderTop: "1px solid #21262d" }}>
                <td style={{ padding: "12px 16px" }}>{req.username}</td>
                <td style={{ padding: "12px 16px", color: "var(--accent2)" }}>{req.amount}</td>
                <td style={{ padding: "12px 16px" }}>{req.method}</td>
                <td style={{ padding: "12px 16px", fontSize: 11 }}>{JSON.stringify(req.details)}</td>
                <td style={{ padding: "12px 16px" }}>
                   <span style={{ 
                     padding: "2px 6px", borderRadius: 4, fontSize: 11,
                     background: req.status === "pending" ? "var(--surface2)" : req.status === "approved" ? "var(--green)" : "var(--red)"
                   }}>{req.status.toUpperCase()}</span>
                </td>
                <td style={{ padding: "12px 16px" }}>
                  {req.status === "pending" ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      <input 
                        placeholder="Add remark..." 
                        value={remark[req._id] || ""} 
                        onChange={(e) => setRemark({ ...remark, [req._id]: e.target.value })}
                        style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text)", padding: "4px 8px", borderRadius: 4, fontSize: 11 }}
                      />
                      <div style={{ display: "flex", gap: 5 }}>
                        <Btn variant="primary" onClick={() => handleAction(req._id, "approve")}>Approve</Btn>
                        <Btn variant="danger" onClick={() => handleAction(req._id, "reject")}>Reject</Btn>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: "var(--muted)" }}>{req.adminRemark}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DepositsTab({ token }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [alert, setAlert]       = useState({ type: "", msg: "" });
  const [remark, setRemark]     = useState({});

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/admin/deposits`, { headers: adminHeaders(token) });
      const data = await res.json();
      setRequests(data);
    } catch (err) { setAlert({ type: "error", msg: "Failed to fetch" }); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleAction = async (requestId, action) => {
    try {
      const res = await fetch(`${API}/admin/deposit/action`, {
        method: "POST", headers: adminHeaders(token),
        body: JSON.stringify({ requestId, action, remark: remark[requestId] || "" })
      });
      const data = await res.json();
      if (res.ok) {
        setAlert({ type: "success", msg: data.message });
        fetchRequests();
      } else {
        setAlert({ type: "error", msg: data.message });
      }
    } catch (err) { setAlert({ type: "error", msg: "Action failed" }); }
  };

  return (
    <div>
      <Alert type={alert.type} msg={alert.msg} onClose={() => setAlert({ type: "", msg: "" })} />
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--surface2)" }}>
              {["User", "Amount", "Method", "Tx ID", "Status", "Actions"].map(h => (
                <th key={h} style={{ padding: "12px 16px", fontSize: 12, color: "var(--muted)", textAlign: "left" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} style={{ padding: 20, textAlign: "center" }}>Loading...</td></tr> : requests.map(req => (
              <tr key={req._id} style={{ borderTop: "1px solid #21262d" }}>
                <td style={{ padding: "12px 16px" }}>{req.username}</td>
                <td style={{ padding: "12px 16px", color: "var(--green)", fontWeight: "bold" }}>+{req.amount}</td>
                <td style={{ padding: "12px 16px" }}>{req.method}</td>
                <td style={{ padding: "12px 16px", fontSize: 11 }}>{req.transactionId || "N/A"}</td>
                <td style={{ padding: "12px 16px" }}>
                   <span style={{ 
                     padding: "2px 6px", borderRadius: 4, fontSize: 11,
                     background: req.status === "pending" ? "var(--surface2)" : req.status === "approved" ? "var(--green)" : "var(--red)"
                   }}>{req.status.toUpperCase()}</span>
                </td>
                <td style={{ padding: "12px 16px" }}>
                  {req.status === "pending" ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      <input 
                        placeholder="Add remark..." 
                        value={remark[req._id] || ""} 
                        onChange={(e) => setRemark({ ...remark, [req._id]: e.target.value })}
                        style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text)", padding: "4px 8px", borderRadius: 4, fontSize: 11 }}
                      />
                      <div style={{ display: "flex", gap: 5 }}>
                        <Btn variant="primary" onClick={() => handleAction(req._id, "approve")}>Approve</Btn>
                        <Btn variant="danger" onClick={() => handleAction(req._id, "reject")}>Reject</Btn>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: "var(--muted)" }}>{req.adminRemark}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Fraud Hub Tab ─────────────────────────────────────────────────────────────
function FraudHubTab({ token }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/fraud/alerts`, { headers: adminHeaders(token) });
      const data = await res.json();
      setAlerts(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const chartData = React.useMemo(() => {
    const days = Array.from({length: 7}, (_, i) => {
      const d = new Date(); d.setDate(d.getDate() - (6 - i));
      return { date: d.toLocaleDateString(undefined, {month: 'short', day: 'numeric'}), count: 0 };
    });
    alerts.forEach(a => {
       const d = new Date(a.timestamp).toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
       const day = days.find(x => x.date === d);
       if (day) day.count++;
    });
    const max = Math.max(...days.map(d => d.count), 1);
    return { days, max };
  }, [alerts]);

  const updateStatus = async (id, status) => {
    try {
      await fetch(`${API}/admin/fraud/alerts/${id}/status`, {
        method: "POST", headers: adminHeaders(token), body: JSON.stringify({ status })
      });
      fetchAlerts();
    } catch (err) { console.error(err); }
  };

  const takeAction = async (username, actionType) => {
    try {
      let res, data;
      if (actionType === "hold") {
        res = await fetch(`${API}/admin/fraud/transactions/hold`, {
          method: "POST", headers: adminHeaders(token), body: JSON.stringify({ username })
        });
      } else {
        res = await fetch(`${API}/admin/fraud/user/action`, {
          method: "POST", headers: adminHeaders(token), body: JSON.stringify({ username, action: actionType })
        });
      }
      data = await res.json();
      setMsg({ type: "success", text: data.message });
      fetchAlerts();
    } catch (err) {
      setMsg({ type: "error", text: "Action failed" });
    }
  };

  return (
    <div>
      <Alert type={msg.type} msg={msg.text} onClose={() => setMsg({ type: "", text: "" })} />
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, alignItems: "center" }}>
         <h2 style={{ margin: 0, fontSize: 18, color: "var(--text)" }}>Fraud Alerts</h2>
         <Btn variant="ghost" onClick={fetchAlerts}>🔄 Refresh</Btn>
      </div>

      {alerts.length > 0 && (
        <div style={{ marginBottom: 24, padding: 16, background: "var(--surface2)", borderRadius: 12 }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 14, color: "var(--muted)", textTransform: "uppercase" }}>7-Day Alert Trend</h3>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 80 }}>
            {chartData.days.map(d => (
              <div key={d.date} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div style={{ 
                  width: "100%", maxWidth: 40, background: d.count > 0 ? "var(--red)" : "var(--border)", 
                  height: `${(d.count / chartData.max) * 60}px`, borderRadius: "4px 4px 0 0", transition: "height 0.3s"
                }} title={`${d.count} alerts`} />
                <div style={{ fontSize: 10, color: "var(--muted)" }}>{d.date}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--surface2)" }}>
              {["Time", "User", "Rule", "Severity", "Status", "Mitigation"].map(h => (
                <th key={h} style={{ padding: "12px 16px", fontSize: 12, color: "var(--muted)", textAlign: "left" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} style={{ padding: 20, textAlign: "center" }}>Loading...</td></tr> : alerts.length === 0 ? <tr><td colSpan={6} style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>No fraud alerts.</td></tr> : alerts.map(a => (
              <tr key={a._id} style={{ borderTop: "1px solid #21262d" }}>
                <td style={{ padding: "12px 16px", fontSize: 11, color: "var(--muted)" }}>{new Date(a.timestamp).toLocaleString()}</td>
                <td style={{ padding: "12px 16px", fontWeight: "bold", color: "var(--accent)" }}>{a.username}</td>
                <td style={{ padding: "12px 16px", fontSize: 13, color: "var(--text)" }}>{a.rule}</td>
                <td style={{ padding: "12px 16px", fontWeight: "bold", color: a.severity === "High" ? "var(--red)" : "var(--accent2)" }}>{a.severity}</td>
                <td style={{ padding: "12px 16px" }}>
                  <select 
                    value={a.status} 
                    onChange={e => updateStatus(a._id, e.target.value)}
                    style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text)", padding: "4px", borderRadius: 4, fontSize: 12 }}
                  >
                    <option>Pending</option>
                    <option>Investigating</option>
                    <option>Resolved (Fraud)</option>
                    <option>False Positive</option>
                  </select>
                </td>
                <td style={{ padding: "12px 16px" }}>
                   <div style={{ display: "flex", gap: 5 }}>
                     <Btn variant="danger" onClick={() => takeAction(a.username, "suspended")}>Suspend</Btn>
                     <Btn variant="blue" onClick={() => takeAction(a.username, "hold")}>Hold Txs</Btn>
                     <Btn variant="danger" onClick={() => takeAction(a.username, "banned")}>Ban</Btn>
                   </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Settings Tab ────────────────────────────────────────────────────────────
function SettingsTab({ token }) {
  const [settings, setSettings] = useState({
    fraud_max_withdrawals_24h: 3,
    fraud_max_bet_amount: 50000,
    fraud_max_bets_1h: 20
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/settings`, { headers: adminHeaders(token) });
      const data = await res.json();
      const s = {
        fraud_max_withdrawals_24h: 3,
        fraud_max_bet_amount: 50000,
        fraud_max_bets_1h: 20
      };
      if (Array.isArray(data)) {
        data.forEach(d => { if (s[d.key] !== undefined) s[d.key] = d.value; });
      }
      setSettings(s);
    } catch (err) { console.error(err); }
  }, [token]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true); setMsg({ type: "", text: "" });
    try {
      for (const [key, value] of Object.entries(settings)) {
        await fetch(`${API}/admin/settings`, {
          method: "POST", headers: adminHeaders(token),
          body: JSON.stringify({ key, value: Number(value) })
        });
      }
      setMsg({ type: "success", text: "Settings updated successfully" });
    } catch (err) { setMsg({ type: "error", text: "Failed to update settings" }); }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 600 }}>
      <Alert type={msg.type} msg={msg.text} onClose={() => setMsg({ type: "", text: "" })} />
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 24 }}>
        <h2 style={{ margin: "0 0 20px", fontSize: 18 }}>System & Fraud Settings</h2>
        <form onSubmit={handleSave}>
          <Field label="Max Withdrawals in 24 Hours (Velocity Check)">
            <Input type="number" min={1} value={settings.fraud_max_withdrawals_24h} onChange={e => setSettings({...settings, fraud_max_withdrawals_24h: e.target.value})} required />
          </Field>
          <Field label="Max Allowed Bet Amount (Abnormal Bet Size Check)">
            <Input type="number" min={100} value={settings.fraud_max_bet_amount} onChange={e => setSettings({...settings, fraud_max_bet_amount: e.target.value})} required />
          </Field>
          <Field label="Max Bets in 1 Hour (Velocity Check)">
            <Input type="number" min={1} value={settings.fraud_max_bets_1h} onChange={e => setSettings({...settings, fraud_max_bets_1h: e.target.value})} required />
          </Field>
          <Btn variant="primary" type="submit" disabled={loading} style={{ width: "100%", marginTop: 12 }}>
            {loading ? "Saving..." : "Save Settings"}
          </Btn>
        </form>
      </div>
    </div>
  );
}

// ── Change Credentials Tab ────────────────────────────────────────────────────
function CredentialsTab({ token, onLogout }) {
  const [form, setForm] = useState({ currentUsername: "", currentPassword: "", newUsername: "", newPassword: "", confirmPassword: "" });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert]     = useState({ type: "", msg: "" });

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setAlert({ type: "", msg: "" });
    if (form.newPassword !== form.confirmPassword) {
      setAlert({ type: "error", msg: "New passwords do not match" }); return;
    }
    if (form.newPassword.length < 6) {
      setAlert({ type: "error", msg: "New password must be at least 6 characters" }); return;
    }
    setLoading(true);
    try {
      const res  = await fetch(`${API}/admin/change-credentials`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          currentUsername: form.currentUsername,
          currentPassword: form.currentPassword,
          newUsername: form.newUsername,
          newPassword: form.newPassword,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setAlert({ type: "success", msg: `${data.message} — You will be logged out now.` });
      setTimeout(() => onLogout(), 3000);
    } catch (err) {
      setAlert({ type: "error", msg: err.message });
    }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 420 }}>
      <Alert type={alert.type} msg={alert.msg} onClose={() => setAlert({ type: "", msg: "" })} />

      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 24 }}>
        <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 0, marginBottom: 20, lineHeight: 1.6 }}>
          Change your admin username and password. You will be logged out after a successful change.
          <br /><br />
          <strong style={{ color: "var(--accent2)" }}>⚠️ Important:</strong> Also update <code style={{ background: "var(--surface2)", padding: "2px 6px", borderRadius: 4 }}>ADMIN_USERNAME</code> and <code style={{ background: "var(--surface2)", padding: "2px 6px", borderRadius: 4 }}>ADMIN_PASSWORD</code> in your backend <code style={{ background: "var(--surface2)", padding: "2px 6px", borderRadius: 4 }}>.env</code> file to make the change permanent after a server restart.
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ borderBottom: "1px solid var(--border)", paddingBottom: 16, marginBottom: 16 }}>
            <p style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", margin: "0 0 12px" }}>Current Credentials</p>
            <Field label="Current Username">
              <Input name="currentUsername" value={form.currentUsername} onChange={handleChange} placeholder="Your current username" required />
            </Field>
            <Field label="Current Password">
              <Input name="currentPassword" type="password" value={form.currentPassword} onChange={handleChange} placeholder="Your current password" required />
            </Field>
          </div>

          <p style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", margin: "0 0 12px" }}>New Credentials</p>
          <Field label="New Username">
            <Input name="newUsername" value={form.newUsername} onChange={handleChange} placeholder="New username" required />
          </Field>
          <Field label="New Password">
            <Input name="newPassword" type="password" value={form.newPassword} onChange={handleChange} placeholder="Min 6 characters" required />
          </Field>
          <Field label="Confirm New Password">
            <Input name="confirmPassword" type="password" value={form.confirmPassword} onChange={handleChange} placeholder="Re-enter new password" required />
          </Field>

          <Btn variant="primary" type="submit" disabled={loading} style={{ width: "100%", padding: "12px", fontSize: 14 }}>
            {loading ? "Updating..." : "Update Credentials"}
          </Btn>
        </form>
      </div>
    </div>
  );
}

// ── Slots Tab ────────────────────────────────────────────────────────────
function SlotsTab({ token }) {
  const [activeTiers, setActiveTiers] = useState([]);
  const [globalRake, setGlobalRake] = useState(10);
  const [activePools, setActivePools] = useState([]);
  const [newTier, setNewTier] = useState("");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ type: "", msg: "" });

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/slots/settings`);
      const data = await res.json();
      if (res.ok) {
        setActiveTiers(data.activeTiers || []);
        setGlobalRake(data.globalRake !== undefined ? data.globalRake : 10);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchActivePools = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/slots/active-pools`, {
        headers: adminHeaders(token)
      });
      const data = await res.json();
      if (res.ok) {
        setActivePools(data || []);
      }
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
    fetchActivePools();
  }, [fetchSettings, fetchActivePools]);

  const handleSaveSettings = async () => {
    setLoading(true);
    setAlert({ type: "", msg: "" });
    try {
      const res = await fetch(`${API}/admin/slots/settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...adminHeaders(token)
        },
        body: JSON.stringify({ activeTiers, globalRake })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setAlert({ type: "success", msg: "Slot settings updated successfully!" });
    } catch (err) {
      setAlert({ type: "error", msg: err.message });
    }
    setLoading(false);
  };

  const handleAddTier = () => {
    const tierVal = parseInt(newTier);
    if (!tierVal || tierVal <= 0) return;
    if (activeTiers.includes(tierVal)) return;
    setActiveTiers([...activeTiers, tierVal].sort((a, b) => a - b));
    setNewTier("");
  };

  const handleRemoveTier = (tier) => {
    setActiveTiers(activeTiers.filter(t => t !== tier));
  };

  const handleSettlePool = async (matchId, tier, winningTeam, action) => {
    if (action === "settle" && !winningTeam) {
      setAlert({ type: "error", msg: "Please specify the winning team for settlement." });
      return;
    }
    if (!window.confirm(`Are you sure you want to ${action} the ${tier}-pebble pool for match ${matchId}?`)) {
      return;
    }
    setLoading(true);
    setAlert({ type: "", msg: "" });
    try {
      const res = await fetch(`${API}/admin/slots/settle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...adminHeaders(token)
        },
        body: JSON.stringify({ matchId, tier, winningTeam, action })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setAlert({ type: "success", msg: data.message });
      fetchActivePools();
    } catch (err) {
      setAlert({ type: "error", msg: err.message });
    }
    setLoading(false);
  };

  return (
    <div>
      <Alert type={alert.type} msg={alert.msg} onClose={() => setAlert({ type: "", msg: "" })} />

      {/* Global Slot Settings Card */}
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 24, marginBottom: 24 }}>
        <h3 style={{ margin: "0 0 16px", color: "var(--text)" }}>⚙️ Global Slot Settings</h3>
        
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: "var(--muted)", display: "block", marginBottom: 8, fontWeight: 600 }}>ACTIVE ENTRY TIERS</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            {activeTiers.map(tier => (
              <span key={tier} style={{
                background: "var(--surface2)", border: "1px solid var(--border)",
                padding: "6px 12px", borderRadius: 20, display: "flex", alignItems: "center", gap: 8, fontSize: 13
              }}>
                🪙 {tier} pebbles
                <button onClick={() => handleRemoveTier(tier)} style={{
                  background: "none", border: "none", color: "var(--danger)", cursor: "pointer", fontWeight: "bold", fontSize: 12, padding: 0
                }}>✕</button>
              </span>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, maxWidth: 260 }}>
            <Input type="number" placeholder="New Tier (e.g. 200)" value={newTier} onChange={e => setNewTier(e.target.value)} />
            <Btn variant="primary" onClick={handleAddTier}>+ Add</Btn>
          </div>
        </div>

        <div style={{ marginBottom: 20, maxWidth: 200 }}>
          <Field label="GLOBAL HOUSE RAKE %">
            <Input type="number" step="0.1" value={globalRake} onChange={e => setGlobalRake(e.target.value)} />
          </Field>
        </div>

        <Btn variant="primary" onClick={handleSaveSettings} disabled={loading}>💾 Save Settings</Btn>
      </div>

      {/* Live Match Pools Card */}
      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 24 }}>
        <h3 style={{ margin: "0 0 8px", color: "var(--text)" }}>🏟️ Active Live Match Pools</h3>
        <p style={{ fontSize: 13, color: "var(--muted)", margin: "0 0 20px" }}>
          View current stakes and settle or refund live betting pools directly.
        </p>

        {activePools.length === 0 ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: "var(--muted)", fontSize: 14 }}>
            No active live betting pools currently in play.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {activePools.map((pool, idx) => {
              return (
                <div key={idx} style={{
                  background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 8, padding: 16,
                  display: "flex", flexDirection: "column", gap: 12
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <h4 style={{ margin: "0 0 4px", fontSize: 15, color: "var(--text)" }}>{pool.matchLabel}</h4>
                      <span style={{
                        background: "rgba(255,165,0,0.15)", color: "orange", border: "1px solid rgba(255,165,0,0.3)",
                        padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: "bold"
                      }}>
                        SLOT: {pool.tier} PEBBLES
                      </span>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 14, fontWeight: "bold", color: "var(--accent)" }}>Total Stakes: {pool.totalStake} pebbles</div>
                      <div style={{ fontSize: 12, color: "var(--muted)" }}>{pool.betsCount} active wagers placed</div>
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, background: "var(--surface)", padding: 12, borderRadius: 6 }}>
                    <div>
                      <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600, textTransform: "uppercase" }}>Stakes on Team A</div>
                      <div style={{ fontSize: 14, fontWeight: "bold", color: "#388bfd" }}>{pool.stakesTeamA} pebbles</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600, textTransform: "uppercase" }}>Stakes on Team B</div>
                      <div style={{ fontSize: 14, fontWeight: "bold", color: "#ff7b72" }}>{pool.stakesTeamB} pebbles</div>
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <select id={`winner-select-${idx}`} style={{
                      background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text)",
                      padding: "8px 12px", borderRadius: 6, fontSize: 13, outline: "none", cursor: "pointer"
                    }}>
                      <option value="">-- Select Winner --</option>
                      <option value="team1">Team A (Yes / Over)</option>
                      <option value="team2">Team B (No / Under)</option>
                    </select>

                    <Btn variant="primary" onClick={() => {
                      const sel = document.getElementById(`winner-select-${idx}`);
                      handleSettlePool(pool.matchId, pool.tier, sel ? sel.value : "", "settle");
                    }} disabled={loading}> Settle Pool</Btn>

                    <Btn variant="danger" onClick={() => {
                      handleSettlePool(pool.matchId, pool.tier, "", "refund");
                    }} disabled={loading}>↩️ Refund All</Btn>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Backup & Restore Tab ─────────────────────────────────────────────────────
function BackupTab({ token }) {
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ type: "", msg: "" });
  const [importPreview, setImportPreview] = useState(null);
  const [importData, setImportData] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [lastBackup, setLastBackup] = useState(() => localStorage.getItem("cw-last-backup") || null);

  const handleExportJSON = async () => {
    setLoading(true);
    setAlert({ type: "", msg: "" });
    try {
      const res = await fetch(`${API}/admin/backup/export`, {
        headers: adminHeaders(token),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Export failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `crickwager-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      const now = new Date().toISOString();
      localStorage.setItem("cw-last-backup", now);
      setLastBackup(now);
      setAlert({ type: "success", msg: "✅ JSON backup downloaded successfully!" });
    } catch (err) {
      setAlert({ type: "error", msg: err.message });
    }
    setLoading(false);
  };

  const handleExportCSV = async () => {
    setLoading(true);
    setAlert({ type: "", msg: "" });
    try {
      const res = await fetch(`${API}/admin/backup/export-csv`, {
        headers: adminHeaders(token),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "CSV export failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `crickwager-backup-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setAlert({ type: "success", msg: "✅ CSV backup downloaded successfully!" });
    } catch (err) {
      setAlert({ type: "error", msg: err.message });
    }
    setLoading(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAlert({ type: "", msg: "" });
    setImportPreview(null);
    setImportData(null);
    setConfirmRestore(false);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!data._meta || !data._meta.version) {
          setAlert({ type: "error", msg: "Invalid backup file: missing _meta or version." });
          return;
        }
        // Build preview
        const preview = {};
        for (const [key, value] of Object.entries(data)) {
          if (key !== "_meta" && Array.isArray(value)) {
            preview[key] = value.length;
          }
        }
        setImportPreview({ meta: data._meta, counts: preview });
        setImportData(data);
      } catch (err) {
        setAlert({ type: "error", msg: "Failed to parse backup file: " + err.message });
      }
    };
    reader.readAsText(file);
  };

  const handleRestore = async () => {
    if (!importData) return;
    setLoading(true);
    setAlert({ type: "", msg: "" });
    try {
      const res = await fetch(`${API}/admin/backup/import`, {
        method: "POST",
        headers: adminHeaders(token),
        body: JSON.stringify(importData),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Import failed");
      setAlert({ type: "success", msg: `✅ ${result.message}` });
      setImportPreview(null);
      setImportData(null);
      setConfirmRestore(false);
    } catch (err) {
      setAlert({ type: "error", msg: err.message });
    }
    setLoading(false);
  };

  return (
    <div>
      <Alert type={alert.type} msg={alert.msg} onClose={() => setAlert({ type: "", msg: "" })} />

      {/* Last backup info */}
      {lastBackup && (
        <div style={{
          background: "var(--surface2)", border: "1px solid var(--border)",
          borderRadius: 10, padding: "10px 14px", marginBottom: 20,
          fontSize: 12, color: "var(--muted)",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          📅 Last backup: <strong style={{ color: "var(--text)" }}>{new Date(lastBackup).toLocaleString()}</strong>
        </div>
      )}

      {/* Export Section */}
      <div style={{
        background: "var(--surface)", border: "1px solid var(--border)",
        borderRadius: 14, padding: 20, marginBottom: 24,
      }}>
        <h3 style={{ margin: "0 0 6px", fontSize: 16, color: "var(--text)" }}>📦 Export Backup</h3>
        <p style={{ fontSize: 12, color: "var(--muted)", margin: "0 0 16px" }}>
          Download a complete backup of all platform data. JSON is recommended for restoration; CSV is for human-readable inspection.
        </p>
        <div style={{ display: "flex", gap: 12 }}>
          <Btn variant="primary" onClick={handleExportJSON} disabled={loading}>
            {loading ? "Exporting..." : "⬇️ Download JSON Backup"}
          </Btn>
          <Btn variant="ghost" onClick={handleExportCSV} disabled={loading}>
            📊 Download CSV
          </Btn>
        </div>
      </div>

      {/* Import Section */}
      <div style={{
        background: "var(--surface)", border: "1px solid var(--border)",
        borderRadius: 14, padding: 20,
      }}>
        <h3 style={{ margin: "0 0 6px", fontSize: 16, color: "var(--text)" }}>📂 Import & Restore</h3>
        <p style={{ fontSize: 12, color: "var(--muted)", margin: "0 0 16px" }}>
          Upload a JSON backup file to restore all platform data. <strong style={{ color: "var(--red)" }}>⚠️ This will replace all existing data.</strong>
        </p>

        <Field label="Select Backup File (.json)">
          <input
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 8,
              background: "var(--surface2)", border: "1px solid var(--border)",
              color: "var(--text)", fontSize: 13, cursor: "pointer",
            }}
          />
        </Field>

        {/* Preview */}
        {importPreview && (
          <div style={{
            background: "var(--surface2)", border: "1px solid var(--border)",
            borderRadius: 10, padding: 16, marginBottom: 16,
          }}>
            <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              📋 Backup Preview
            </div>
            <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 12 }}>
              Exported: {new Date(importPreview.meta.exportedAt).toLocaleString()} • Version: {importPreview.meta.version}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8 }}>
              {Object.entries(importPreview.counts).map(([key, count]) => (
                <div key={key} style={{
                  background: "var(--surface)", border: "1px solid var(--border)",
                  borderRadius: 8, padding: "8px 12px",
                }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "var(--accent)" }}>{count}</div>
                  <div style={{ fontSize: 10, color: "var(--muted)", textTransform: "capitalize" }}>{key.replace(/([A-Z])/g, " $1")}</div>
                </div>
              ))}
            </div>

            {!confirmRestore ? (
              <Btn variant="danger" onClick={() => setConfirmRestore(true)} style={{ marginTop: 16, width: "100%" }}>
                ⚠️ Proceed to Restore
              </Btn>
            ) : (
              <div style={{
                marginTop: 16, padding: 14,
                background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)",
                borderRadius: 10,
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--red)", marginBottom: 8 }}>
                  ⚠️ This will PERMANENTLY DELETE all existing data and replace it with this backup.
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <Btn variant="danger" onClick={handleRestore} disabled={loading}>
                    {loading ? "Restoring..." : "🔥 CONFIRM RESTORE"}
                  </Btn>
                  <Btn variant="ghost" onClick={() => setConfirmRestore(false)}>
                    Cancel
                  </Btn>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function EmergencyTab({ token, onLogout }) {
  const [adminPassword, setAdminPassword] = useState("");
  const [emergencyKey, setEmergencyKey]   = useState("");
  const [confirmationText, setConfirmationText] = useState("");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert]     = useState({ type: "", msg: "" });

  const handleSelfDestruct = async (e) => {
    e.preventDefault();
    setAlert({ type: "", msg: "" });

    if (confirmationText !== "PURGE ALL DATA") {
      setAlert({ type: "error", msg: "You must type 'PURGE ALL DATA' exactly to proceed." });
      return;
    }

    if (!window.confirm("🚨 CRITICAL WARNING: Triggering the system-wide nuclear self-destruct is IRREVERSIBLE. All user tables, wagers, device fingerprints, and logs will be permanently deleted and administrative lock out will activate. Are you absolutely sure?")) {
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/nuclear-purge`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...adminHeaders(token)
        },
        body: JSON.stringify({ adminPassword, emergencyKey, confirmationText })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setAlert({ type: "success", msg: "⚡ SYSTEM WIPE INITIATED. ALL SENSITIVE DATA HAS BEEN ERASED. Locking session..." });
      setTimeout(() => {
        sessionStorage.clear();
        localStorage.clear();
        onLogout();
        window.location.reload();
      }, 4000);
    } catch (err) {
      setAlert({ type: "error", msg: err.message || "Self-destruct process failed." });
    }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 540 }}>
      <Alert type={alert.type} msg={alert.msg} onClose={() => setAlert({ type: "", msg: "" })} />

      <div style={{
        background: "var(--surface)",
        border: "1px solid rgba(248,113,113,0.3)",
        borderRadius: 16,
        padding: 28,
        boxShadow: "0 10px 40px rgba(239, 68, 68, 0.15)",
        position: "relative",
        overflow: "hidden"
      }}>
        {/* Glow accent */}
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0, height: 4,
          background: "linear-gradient(90deg, #ef4444, #f87171, #ef4444)"
        }}></div>

        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16 }}>
          <span style={{ fontSize: 24 }}>🚨</span>
          <h2 style={{ margin: 0, color: "var(--red)", fontSize: 20, fontWeight: 800, letterSpacing: "-0.5px" }}>Emergency Nuclear Purge</h2>
        </div>

        <p style={{ fontSize: 13, color: "var(--muted)", marginTop: 0, marginBottom: 24, lineHeight: 1.6 }}>
          This interface initiates an **irreversible system self-destruct**. It will completely scramble administrative access credentials, erase IP logs, remove hardware/browser fingerprints, wipe all registered user profiles, delete bet records, and permanently place the platform in a hardlocked offline maintenance mode.
        </p>

        <form onSubmit={handleSelfDestruct}>
          <Field label="Step 1: Enter Emergency Secret Key">
            <Input 
              type="password" 
              placeholder="EMERGENCY_KILL_KEY from .env" 
              value={emergencyKey} 
              onChange={e => setEmergencyKey(e.target.value)} 
              required 
            />
          </Field>

          <Field label="Step 2: Enter Portal Admin Password">
            <Input 
              type="password" 
              placeholder="Your current admin credentials password" 
              value={adminPassword} 
              onChange={e => setAdminPassword(e.target.value)} 
              required 
            />
          </Field>

          <Field label="Step 3: Type 'PURGE ALL DATA' to Confirm">
            <Input 
              type="text" 
              placeholder="Type PURGE ALL DATA" 
              value={confirmationText} 
              onChange={e => setConfirmationText(e.target.value)} 
              style={{ border: confirmationText === "PURGE ALL DATA" ? "1px solid var(--green)" : "1px solid var(--border)", fontFamily: "monospace" }}
              required 
            />
          </Field>

          <Btn 
            variant="danger" 
            type="submit" 
            disabled={loading || confirmationText !== "PURGE ALL DATA"} 
            style={{
              width: "100%",
              padding: "14px",
              fontSize: 14,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "1px",
              marginTop: 10,
              background: confirmationText === "PURGE ALL DATA" ? "#ef4444" : "rgba(239, 68, 68, 0.1)",
              borderColor: "#ef4444",
              color: "#fff"
            }}
          >
            {loading ? "Wiping Systems..." : "🔥 Execute Self-Destruct"}
          </Btn>
        </form>
      </div>
    </div>
  );
}

// ── Scraper Tab ───────────────────────────────────────────────────────────────
function ScraperTab({ token }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const handlePullData = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/scraper/run`, { headers: adminHeaders(token) });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Failed to run scraper");
      setData(result.matches);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div>
      <h2 style={{ fontSize: 18, marginBottom: 16 }}>🕸️ Emergency Scraper Control</h2>
      <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 20 }}>
        Trigger the backend scraper to fetch live scores and matches from the ESPNcricinfo XML feed. 
        This is useful if the primary WebSocket connection fails.
      </p>

      <div style={{ background: "var(--surface)", border: "1px solid var(--border)", padding: 20, borderRadius: 12, marginBottom: 20 }}>
        <Field label="Target Source">
          <Input type="text" value="https://www.espncricinfo.com/rss/livescores.xml" readOnly style={{ background: "var(--surface2)", opacity: 0.7 }} />
        </Field>
        
        <Btn variant="primary" onClick={handlePullData} disabled={loading}>
          {loading ? "Scraping Data..." : "⚡ Pull Live Data"}
        </Btn>
      </div>

      <Alert type="error" msg={error} onClose={() => setError("")} />

      {data && (
        <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)", background: "rgba(0,0,0,0.2)" }}>
            <h3 style={{ margin: 0, fontSize: 14 }}>Scraped Results ({data.length})</h3>
          </div>
          {data.length === 0 ? (
            <div style={{ padding: 20, color: "var(--muted)", textAlign: "center" }}>No matches found on the feed right now.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "var(--surface2)", textAlign: "left" }}>
                    <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)", fontWeight: 600, color: "var(--muted)" }}>MATCH TITLE</th>
                    <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)", fontWeight: 600, color: "var(--muted)" }}>STATUS / SCORE</th>
                    <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)", fontWeight: 600, color: "var(--muted)" }}>LINK</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((match, i) => (
                    <tr key={match.id || i} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "12px 16px", fontWeight: 500 }}>{match.title}</td>
                      <td style={{ padding: "12px 16px", color: "var(--accent)" }}>{match.statusText}</td>
                      <td style={{ padding: "12px 16px" }}>
                        <a href={match.link} target="_blank" rel="noreferrer" style={{ color: "#58a6ff", textDecoration: "none" }}>View ↗</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── eAdmin Master Command Desk ────────────────────────────────────────────────────
function EAdminTab({ token }) {
  const h = adminHeaders(token);
  const [msg, setMsg]   = useState({ type: "", text: "" });

  // Panel 1: Force-Settle
  const [fsRoomId, setFsRoomId]     = useState("");
  const [fsMarket, setFsMarket]     = useState("over_runs");
  const [fsOver, setFsOver]         = useState("");
  const [fsOutcome, setFsOutcome]   = useState("");
  const [fsRake, setFsRake]         = useState("15");
  const [fsResult, setFsResult]     = useState(null);
  const [fsLoading, setFsLoading]   = useState(false);

  const handleForceSettle = async () => {
    if (!fsRoomId || !fsMarket || !fsOutcome) { setMsg({ type: "error", text: "Room ID, Market and Outcome are required" }); return; }
    setFsLoading(true); setFsResult(null);
    try {
      const res  = await fetch(`${API}/admin/rooms/${fsRoomId}/force-settle`, {
        method: "POST", headers: h,
        body: JSON.stringify({ market: fsMarket, targetOver: fsOver ? Number(fsOver) : undefined, forcedOutcome: fsOutcome, rakeOverride: Number(fsRake) / 100 })
      });
      const data = await res.json();
      setMsg(res.ok ? { type: "success", text: data.message } : { type: "error", text: data.message });
      if (res.ok) setFsResult(data);
    } catch (e) { setMsg({ type: "error", text: "Network error" }); }
    setFsLoading(false);
  };

  // Panel 2: Ghost Stakes
  const [gsRoomId, setGsRoomId]     = useState("");
  const [gsMarket, setGsMarket]     = useState("over_runs");
  const [gsOutcome, setGsOutcome]   = useState("");
  const [gsOver, setGsOver]         = useState("");
  const [gsCount, setGsCount]       = useState("3");
  const [gsAmount, setGsAmount]     = useState("");
  const [gsResult, setGsResult]     = useState(null);
  const [gsLoading, setGsLoading]   = useState(false);

  const handleGhostStakes = async () => {
    if (!gsRoomId || !gsMarket || !gsOutcome) { setMsg({ type: "error", text: "Room ID, Market and Outcome are required" }); return; }
    setGsLoading(true); setGsResult(null);
    try {
      const res  = await fetch(`${API}/admin/rooms/${gsRoomId}/inject-ghost-stakes`, {
        method: "POST", headers: h,
        body: JSON.stringify({ market: gsMarket, targetOver: gsOver ? Number(gsOver) : undefined, outcome: gsOutcome, ghostCount: Number(gsCount), ghostAmount: gsAmount ? Number(gsAmount) : undefined })
      });
      const data = await res.json();
      setMsg(res.ok ? { type: "success", text: data.message } : { type: "error", text: data.message });
      if (res.ok) setGsResult(data);
    } catch (e) { setMsg({ type: "error", text: "Network error" }); }
    setGsLoading(false);
  };

  // Panel 3: Market Freeze
  const [mfMatchId, setMfMatchId]   = useState("");
  const [mfLoading, setMfLoading]   = useState(false);

  const handleMarketFreeze = async (freeze) => {
    setMfLoading(true);
    try {
      const res  = await fetch(`${API}/admin/market-freeze`, {
        method: "POST", headers: h,
        body: JSON.stringify({ freeze, matchId: mfMatchId || undefined })
      });
      const data = await res.json();
      setMsg(res.ok ? { type: "success", text: data.message } : { type: "error", text: data.message });
    } catch (e) { setMsg({ type: "error", text: "Network error" }); }
    setMfLoading(false);
  };

  // Panel 4: User Risk Profile
  const [rpUser, setRpUser]           = useState("");
  const [rpData, setRpData]           = useState(null);
  const [rpLoading, setRpLoading]     = useState(false);
  const [rpSaving, setRpSaving]       = useState(false);
  const [rpRiskFlag, setRpRiskFlag]   = useState(false);
  const [rpSuppression, setRpSuppression] = useState(0);
  const [rpDelay, setRpDelay]         = useState(false);
  const [rpGhostErr, setRpGhostErr]   = useState(false);

  const loadRiskProfile = async () => {
    if (!rpUser) return;
    setRpLoading(true); setRpData(null);
    try {
      const res  = await fetch(`${API}/user-risk-profile/${rpUser}`, { headers: h });
      const data = await res.json();
      if (res.ok) {
        setRpData(data);
        setRpRiskFlag(data.riskFlag);
        setRpSuppression(data.oddsSuppression);
        setRpDelay(data.forcedDelay);
        setRpGhostErr(data.ghostErrors);
      } else { setMsg({ type: "error", text: data.message }); }
    } catch (e) { setMsg({ type: "error", text: "Network error" }); }
    setRpLoading(false);
  };

  const saveRiskProfile = async () => {
    if (!rpUser) { setMsg({ type: "error", text: "Enter a username" }); return; }
    setRpSaving(true);
    try {
      const res  = await fetch(`${API}/admin/user-risk-profile`, {
        method: "POST", headers: h,
        body: JSON.stringify({ username: rpUser, riskFlag: rpRiskFlag, oddsSuppression: rpSuppression, forcedDelay: rpDelay, ghostErrors: rpGhostErr })
      });
      const data = await res.json();
      setMsg(res.ok ? { type: "success", text: data.message } : { type: "error", text: data.message });
    } catch (e) { setMsg({ type: "error", text: "Network error" }); }
    setRpSaving(false);
  };

  // Panel 5: DLS Rain Override
  const [dlsMatchId, setDlsMatchId]       = useState("");
  const [dlsAction, setDlsAction]         = useState("fee-refund");
  const [dlsFeePercent, setDlsFeePercent] = useState("10");
  const [dlsOutcome, setDlsOutcome]       = useState("");
  const [dlsLoading, setDlsLoading]       = useState(false);
  const [dlsResult, setDlsResult]         = useState(null);

  const handleDlsOverride = async () => {
    if (!dlsMatchId || !dlsAction) { setMsg({ type: "error", text: "Match ID and Action are required" }); return; }
    setDlsLoading(true); setDlsResult(null);
    try {
      const res  = await fetch(`${API}/admin/dls-rain-override`, {
        method: "POST", headers: h,
        body: JSON.stringify({ matchId: dlsMatchId, action: dlsAction, refundFeePercent: Number(dlsFeePercent), forcedOutcome: dlsOutcome || undefined })
      });
      const data = await res.json();
      setMsg(res.ok ? { type: "success", text: data.message } : { type: "error", text: data.message });
      if (res.ok) setDlsResult(data);
    } catch (e) { setMsg({ type: "error", text: "Network error" }); }
    setDlsLoading(false);
  };

  // Panel 6: Cash-Out Config
  const [coTax, setCoTax]           = useState("15");
  const [coMulti, setCoMulti]       = useState("1.1");
  const [coDisabled, setCoDisabled] = useState(false);
  const [coLoading, setCoLoading]   = useState(false);

  useEffect(() => {
    fetch(`${API}/admin/cashout-config`, { headers: h })
      .then(r => r.json())
      .then(d => { setCoTax(String(d.taxPercent)); setCoMulti(String(d.maxPayoutMultiplier)); setCoDisabled(d.globallyDisabled); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleCashoutConfig = async () => {
    setCoLoading(true);
    try {
      const res  = await fetch(`${API}/admin/cashout-config`, {
        method: "POST", headers: h,
        body: JSON.stringify({ taxPercent: Number(coTax), maxPayoutMultiplier: Number(coMulti), globallyDisabled: coDisabled })
      });
      const data = await res.json();
      setMsg(res.ok ? { type: "success", text: data.message } : { type: "error", text: data.message });
    } catch (e) { setMsg({ type: "error", text: "Network error" }); }
    setCoLoading(false);
  };

  const panelStyle = {
    background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14,
    padding: 20, marginBottom: 20,
  };
  const sectionHead = {
    margin: "0 0 14px", fontSize: 15, fontWeight: 800, letterSpacing: 0.5,
    display: "flex", alignItems: "center", gap: 8,
  };
  const warningBadge = {
    background: "rgba(234,179,8,0.15)", border: "1px solid rgba(234,179,8,0.3)",
    color: "#eab308", borderRadius: 6, padding: "2px 8px", fontSize: 10, fontWeight: 700,
  };
  const infoRow = {
    display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12,
  };

  return (
    <div>
      {/* Header banner */}
      <div style={{
        background: "linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(245,158,11,0.08) 100%)",
        border: "1px solid rgba(239,68,68,0.25)", borderRadius: 14,
        padding: "16px 20px", marginBottom: 20,
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <span style={{ fontSize: 28 }}>⚔️</span>
        <div>
          <div style={{ fontWeight: 900, fontSize: 17, color: "var(--text)" }}>eAdmin Master Command Desk</div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>
            Extreme profit-protection controls. All actions are permanent and unreversible to users.
          </div>
        </div>
        <span style={{ ...warningBadge, marginLeft: "auto", padding: "4px 10px", fontSize: 11 }}>⚠️ ADMIN EYES ONLY</span>
      </div>

      <Alert type={msg.type} msg={msg.text} onClose={() => setMsg({ type: "", text: "" })} />

      {/* Panel 1: Force-Settle */}
      <div style={panelStyle}>
        <h3 style={{ ...sectionHead, color: "var(--red)" }}>🎯 Panel 1 — Force-Settle Room Pool <span style={warningBadge}>IRREVERSIBLE</span></h3>
        <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 0, marginBottom: 16 }}>
          Override the natural result of any pending live-room bet pool. House keeps configurable rake. All bets settle to YOUR chosen outcome.
        </p>
        <div style={infoRow}>
          <Field label="Room ID"><Input placeholder="Paste Room _id from DB" value={fsRoomId} onChange={e => setFsRoomId(e.target.value)} /></Field>
          <Field label="Market">
            <select value={fsMarket} onChange={e => setFsMarket(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text)", fontSize: 14 }}>
              <option value="over_runs">Over Runs</option>
              <option value="wicket">Wicket</option>
              <option value="player_runs">Player Runs</option>
              <option value="session_bracket">Session Bracket</option>
              <option value="toss">Toss</option>
            </select>
          </Field>
          <Field label="Target Over (optional)"><Input type="number" placeholder="e.g. 12" value={fsOver} onChange={e => setFsOver(e.target.value)} /></Field>
          <Field label="Forced Outcome (exact string)"><Input placeholder="e.g. over, under, yes, no" value={fsOutcome} onChange={e => setFsOutcome(e.target.value)} /></Field>
        </div>
        <Field label={`House Rake Override (5–35%) — currently ${fsRake}%`}>
          <input type="range" min={5} max={35} step={1} value={fsRake} onChange={e => setFsRake(e.target.value)} style={{ width: "100%" }} />
        </Field>
        <Btn variant="danger" disabled={fsLoading} onClick={handleForceSettle} style={{ width: "100%" }}>
          {fsLoading ? "⏳ Settling..." : `⚡ FORCE SETTLE → "${fsOutcome || "(set outcome)"}" (Rake: ${fsRake}%)`}
        </Btn>
        {fsResult && (
          <div style={{ marginTop: 14, background: "var(--surface2)", borderRadius: 8, padding: 12, fontSize: 12 }}>
            <div style={{ color: "var(--red)", fontWeight: 700, marginBottom: 6 }}>
              Pool: {fsResult.totalPool} PB | Rake: {fsResult.rake} PB | Net: {fsResult.netPool} PB
            </div>
            {(fsResult.settled || []).slice(0, 8).map((s, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid var(--border)", paddingTop: 4, marginTop: 4 }}>
                <span style={{ color: "var(--muted)" }}>{s.username}</span>
                <span style={{ color: s.outcome === "won" ? "var(--green)" : "var(--red)", fontWeight: 700 }}>{s.outcome.toUpperCase()} {s.outcome === "won" ? `+${s.payout}` : ""}</span>
              </div>
            ))}
            {fsResult.settled && fsResult.settled.length > 8 && <div style={{ color: "var(--muted)", fontSize: 11, marginTop: 4 }}>...and {fsResult.settled.length - 8} more</div>}
          </div>
        )}
      </div>

      {/* Panel 2: Ghost Stakes */}
      <div style={panelStyle}>
        <h3 style={{ ...sectionHead, color: "#a78bfa" }}>👻 Panel 2 — Ghost Stake Injection <span style={warningBadge}>HIDDEN</span></h3>
        <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 0, marginBottom: 16 }}>
          Inject invisible ghost bets on any outcome to dilute real winner payouts. Ghost accounts are internal and don't affect visible user counts.
        </p>
        <div style={infoRow}>
          <Field label="Room ID"><Input placeholder="Paste Room _id" value={gsRoomId} onChange={e => setGsRoomId(e.target.value)} /></Field>
          <Field label="Market">
            <select value={gsMarket} onChange={e => setGsMarket(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text)", fontSize: 14 }}>
              <option value="over_runs">Over Runs</option>
              <option value="wicket">Wicket</option>
              <option value="player_runs">Player Runs</option>
              <option value="session_bracket">Session Bracket</option>
              <option value="toss">Toss</option>
            </select>
          </Field>
          <Field label="Inject on Outcome"><Input placeholder="over, under, yes, no..." value={gsOutcome} onChange={e => setGsOutcome(e.target.value)} /></Field>
          <Field label="Target Over (optional)"><Input type="number" placeholder="e.g. 8" value={gsOver} onChange={e => setGsOver(e.target.value)} /></Field>
          <Field label={`Ghost Count: ${gsCount}`}><input type="range" min={1} max={10} value={gsCount} onChange={e => setGsCount(e.target.value)} style={{ width: "100%" }} /></Field>
          <Field label="Ghost Bet Amount (PB each)"><Input type="number" placeholder="Leave blank = room entry fee" value={gsAmount} onChange={e => setGsAmount(e.target.value)} /></Field>
        </div>
        <Btn variant="blue" disabled={gsLoading} onClick={handleGhostStakes} style={{ width: "100%" }}>
          {gsLoading ? "⏳ Injecting..." : `👻 INJECT ${gsCount} GHOSTS on "${gsOutcome || "(set outcome)"}"`}
        </Btn>
        {gsResult && (
          <div style={{ marginTop: 12, background: "var(--surface2)", borderRadius: 8, padding: 10, fontSize: 12, color: "var(--muted)" }}>
            {gsResult.injected?.map((g, i) => <span key={i} style={{ marginRight: 10 }}>{g.username} ({g.amount} PB)</span>)}
          </div>
        )}
      </div>

      {/* Panel 3: Market Freeze */}
      <div style={panelStyle}>
        <h3 style={{ ...sectionHead, color: "#60a5fa" }}>🧊 Panel 3 — Market Freeze Control</h3>
        <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 0, marginBottom: 16 }}>
          Instantly freeze or unfreeze betting markets. Leave Match ID blank to freeze ALL markets globally.
        </p>
        <Field label="Match ID (optional — blank = global)">
          <Input placeholder="e.g. ipl_2025_mi_vs_csk or leave blank" value={mfMatchId} onChange={e => setMfMatchId(e.target.value)} />
        </Field>
        <div style={{ display: "flex", gap: 12 }}>
          <Btn variant="danger" disabled={mfLoading} onClick={() => handleMarketFreeze(true)} style={{ flex: 1 }}>
            🔴 FREEZE{mfMatchId ? " MATCH" : " ALL MARKETS"}
          </Btn>
          <Btn variant="primary" disabled={mfLoading} onClick={() => handleMarketFreeze(false)} style={{ flex: 1 }}>
            🟢 UNFREEZE{mfMatchId ? " MATCH" : " ALL MARKETS"}
          </Btn>
        </div>
      </div>

      {/* Panel 4: User Risk Profile */}
      <div style={panelStyle}>
        <h3 style={{ ...sectionHead, color: "#f97316" }}>🎭 Panel 4 — User Risk Profiler <span style={warningBadge}>SILENT</span></h3>
        <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 0, marginBottom: 16 }}>
          Flag high-winners. Suppress displayed odds, add bet delays, or serve silent errors on big bets. User sees nothing unusual.
        </p>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <Input placeholder="Username to profile" value={rpUser} onChange={e => setRpUser(e.target.value)} style={{ flex: 1 }} />
          <Btn variant="ghost" disabled={rpLoading} onClick={loadRiskProfile}>🔍 Load</Btn>
        </div>
        {rpData && (
          <div style={{ background: "var(--surface2)", borderRadius: 10, padding: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
                <input type="checkbox" checked={rpRiskFlag} onChange={e => setRpRiskFlag(e.target.checked)} />
                <span>🚩 Risk Flag (mark as high-winner)</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
                <input type="checkbox" checked={rpDelay} onChange={e => setRpDelay(e.target.checked)} />
                <span>⏱️ Force 3s Bet Delay</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
                <input type="checkbox" checked={rpGhostErr} onChange={e => setRpGhostErr(e.target.checked)} />
                <span>💀 Ghost Errors on Large Bets</span>
              </label>
            </div>
            <Field label={`Odds Suppression: ${rpSuppression}% (0 = normal, 40 = max)`}>
              <input type="range" min={0} max={40} step={1} value={rpSuppression} onChange={e => setRpSuppression(Number(e.target.value))} style={{ width: "100%" }} />
            </Field>
            <Btn variant="danger" disabled={rpSaving} onClick={saveRiskProfile} style={{ width: "100%" }}>
              {rpSaving ? "Saving..." : `💾 APPLY RISK PROFILE TO "${rpUser}"`}
            </Btn>
          </div>
        )}
      </div>

      {/* Panel 5: DLS Rain Override */}
      <div style={panelStyle}>
        <h3 style={{ ...sectionHead, color: "#06b6d4" }}>🌧️ Panel 5 — DLS / Rain Override</h3>
        <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 0, marginBottom: 16 }}>
          When match is interrupted, Admin decides outcome — no automatic refunds. Choose: rollover, deduct-fee-refund, or force-settle.
        </p>
        <div style={infoRow}>
          <Field label="Match ID"><Input placeholder="e.g. ipl_2025_mi_vs_csk" value={dlsMatchId} onChange={e => setDlsMatchId(e.target.value)} /></Field>
          <Field label="Action">
            <select value={dlsAction} onChange={e => setDlsAction(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)", color: "var(--text)", fontSize: 14 }}>
              <option value="fee-refund">💰 Fee-Refund (keep fee%)</option>
              <option value="rollover">🔄 Rollover (keep bets locked)</option>
              <option value="force-settle">⚡ Force-Settle to outcome</option>
            </select>
          </Field>
          {dlsAction === "fee-refund" && (
            <Field label={`Admin Fee on Refund: ${dlsFeePercent}%`}>
              <input type="range" min={0} max={35} value={dlsFeePercent} onChange={e => setDlsFeePercent(e.target.value)} style={{ width: "100%" }} />
            </Field>
          )}
          {dlsAction === "force-settle" && (
            <Field label="Forced Outcome"><Input placeholder="over, under, yes, no..." value={dlsOutcome} onChange={e => setDlsOutcome(e.target.value)} /></Field>
          )}
        </div>
        <Btn variant="blue" disabled={dlsLoading} onClick={handleDlsOverride} style={{ width: "100%" }}>
          {dlsLoading ? "Applying..." : `🌧️ APPLY DLS OVERRIDE (${dlsAction})`}
        </Btn>
        {dlsResult && (
          <div style={{ marginTop: 12, fontSize: 12, color: "var(--muted)", background: "var(--surface2)", borderRadius: 8, padding: 10 }}>
            Affected {dlsResult.results?.length || 0} bets. Fee rate: {((dlsResult.feeRate || 0) * 100).toFixed(1)}%
          </div>
        )}
      </div>

      {/* Panel 6: Cash-Out Tax */}
      <div style={panelStyle}>
        <h3 style={{ ...sectionHead, color: "#10b981" }}>💸 Panel 6 — Cash-Out Tax & Cap</h3>
        <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 0, marginBottom: 16 }}>
          Control the maximum payout multiplier and tax rate that applies to all cash-out events. Disable globally to force full settlement.
        </p>
        <div style={infoRow}>
          <Field label={`Tax on Cash-Out: ${coTax}%`}>
            <input type="range" min={0} max={35} step={1} value={coTax} onChange={e => setCoTax(e.target.value)} style={{ width: "100%" }} />
          </Field>
          <Field label={`Max Payout Multiplier: ${coMulti}x`}>
            <input type="range" min={1.0} max={3.0} step={0.1} value={coMulti} onChange={e => setCoMulti(e.target.value)} style={{ width: "100%" }} />
          </Field>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13, marginBottom: 14 }}>
          <input type="checkbox" checked={coDisabled} onChange={e => setCoDisabled(e.target.checked)} />
          <span>🚫 Globally Disable Cash-Out (force users to wait for settlement)</span>
        </label>
        <Btn variant="primary" disabled={coLoading} onClick={handleCashoutConfig} style={{ width: "100%" }}>
          {coLoading ? "Saving..." : `💾 SAVE CASH-OUT CONFIG (Tax: ${coTax}%, Cap: ${coMulti}x)`}
        </Btn>
      </div>
    </div>
  );
}

// ── Main AdminPanel ───────────────────────────────────────────────────────────
export default function AdminPanel({ onBack }) {
  const [token, setToken]   = useState(() => sessionStorage.getItem("adminToken") || "");
  const [tab, setTab]       = useState("overview");
  const [fraudCount, setFraudCount] = useState(0);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/admin/stats`, { headers: adminHeaders(token) })
      .then(res => res.json())
      .then(data => setFraudCount(data.pendingHighFraudAlerts || 0))
      .catch(console.error);
  }, [token]);

  const handleLogin = (t) => {
    sessionStorage.setItem("adminToken", t);
    setToken(t);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminToken");
    setToken("");
  };

  if (!token) return <AdminLogin onLogin={handleLogin} />;

  const tabs = [
    { key: "overview",    label: "📊 Overview" },
    { key: "users",       label: "👥 Users" },
    { key: "slots",       label: "🪙 Manage Slots" },
    { key: "withdrawals", label: "💸 Withdrawals" },
    { key: "deposits",    label: "📥 Deposits" },
    { key: "fraud",       label: fraudCount > 0 ? `🚨 Fraud Hub (${fraudCount})` : "🚨 Fraud Hub" },
    { key: "settings",    label: "⚙️ Settings" },
    { key: "credentials", label: "🔐 Credentials" },
    { key: "scraper",     label: "🕸️ Scraper" },
    { key: "backup",      label: "📦 Backup" },
    { key: "emergency",   label: "🚨 Emergency" },
    { key: "eadmin",      label: "⚔️ eAdmin" },
  ];

  return (
    <div style={{
      maxWidth: 960, margin: "0 auto", padding: "20px 16px",
      minHeight: "100vh",
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, margin: 0, display: "flex", alignItems: "center", gap: 10, color: "var(--text)" }}>
          {onBack && (
            <button onClick={onBack} style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 20, cursor: "pointer", padding: 0 }}>←</button>
          )}
          Admin Panel
        </h1>
        <Btn variant="danger" onClick={handleLogout}>🚪 Logout</Btn>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24, borderBottom: "1px solid var(--border)", paddingBottom: 0 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: "10px 18px", background: "none", cursor: "pointer",
            fontSize: 13, fontWeight: 600,
            color: tab === t.key ? "var(--text)" : "var(--muted)",
            border: "none",
            borderBottom: tab === t.key ? "2px solid #238636" : "2px solid transparent",
            transition: "all 0.15s",
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {tab === "overview"    && <OverviewTab    token={token} />}
      {tab === "users"       && <UsersTab       token={token} />}
      {tab === "slots"       && <SlotsTab       token={token} />}
      {tab === "withdrawals" && <WithdrawalsTab token={token} />}
      {tab === "deposits"    && <DepositsTab    token={token} />}
      {tab === "fraud"       && <FraudHubTab    token={token} />}
      {tab === "settings"    && <SettingsTab    token={token} />}
      { tab === "credentials" && <CredentialsTab token={token} onLogout={handleLogout} /> }
      { tab === "scraper"     && <ScraperTab     token={token} /> }
      { tab === "backup"      && <BackupTab      token={token} /> }
      { tab === "emergency"   && <EmergencyTab   token={token} onLogout={handleLogout} /> }
      { tab === "eadmin"      && <EAdminTab      token={token} /> }
    </div>
  );
}
